import React from 'react';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { Office } from '../../../models/office';
import { User } from '../../../models/user';
import PricingModeField, { PricingMode } from '../../inventory/shared/PricingModeField';
import { useT } from '../../../i18n';
import { FormSection, FullRow } from '../shared/AdminUi';

/** Campos de una ruta que se editan igual al crearla y al editarla. */
export interface RouteFormValues {
  name: string;
  officeId: string;
  userId: string;
  creditLimit: string;
  discountDays: string;
  insurance: string;
  insuranceRate: string;
  manualInsurance: boolean;
  allowClientCreditLimit: boolean;
  /** Vacío o 0 = apagado; N = no abrir otro préstamo el día en que el cliente pague N o más cuotas. */
  blockNewLoanAfterInstallments: string;
  productPricingMode: PricingMode;
  requireExpensePhoto: boolean;
  /** Topes diarios por persona (antifraude); vacío = sin tope. */
  maxDiscountPerDay: string;
  maxRefinancesPerDay: string;
  maxExpensesPerDay: string;
}

export const EMPTY_ROUTE: RouteFormValues = {
  name: '',
  officeId: '',
  userId: '',
  creditLimit: '',
  discountDays: '0',
  insurance: '0',
  insuranceRate: '0',
  manualInsurance: true,
  allowClientCreditLimit: false,
  blockNewLoanAfterInstallments: '',
  productPricingMode: 'CONSTANT',
  requireExpensePhoto: false,
  maxDiscountPerDay: '',
  maxRefinancesPerDay: '',
  maxExpensesPerDay: '',
};

export type RouteFormErrors = Partial<Record<'name' | 'officeId' | 'userId' | 'creditLimit', string>>;

export const validateRoute = (v: RouteFormValues, required: string): RouteFormErrors => {
  const e: RouteFormErrors = {};
  if (!v.name.trim()) e.name = required;
  if (!v.officeId) e.officeId = required;
  if (!v.userId) e.userId = required;
  if (!v.creditLimit) e.creditLimit = required;
  return e;
};

interface Props {
  values: RouteFormValues;
  errors: RouteFormErrors;
  onChange: (patch: Partial<RouteFormValues>) => void;
  offices: Office[];
  users: User[];
  productOffice: boolean;
}

/** Secciones del formulario de ruta: datos, préstamos, cuota de seguro y topes diarios. */
const RouteFormSections: React.FC<Props> = ({ values: v, errors, onChange, offices, users, productOffice }) => {
  const t = useT();
  const text = (key: keyof RouteFormValues) => (e: React.ChangeEvent<HTMLInputElement>) => onChange({ [key]: e.target.value } as Partial<RouteFormValues>);
  const err = (key: keyof RouteFormErrors) => ({ error: !!errors[key], helperText: errors[key] });

  return (
    <>
      <FormSection title={t('routes.sectionData')}>
        <TextField id="route-name" label={t('common.name')} placeholder={t('routes.namePlaceholder')} value={v.name} onChange={text('name')} inputProps={{ maxLength: 30 }} required fullWidth {...err('name')} />
        <TextField
          id="route-office"
          select
          label={t('routes.office')}
          value={offices.length ? v.officeId : ''}
          onChange={text('officeId')}
          required
          fullWidth
          {...err('officeId')}
        >
          {offices.map((office) => (
            <MenuItem key={office.id} value={String(office.id)}>
              {office.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          id="route-sales-rep"
          select
          label={t('routes.selectSalesRep')}
          value={users.length ? v.userId : ''}
          onChange={text('userId')}
          required
          fullWidth
          {...err('userId')}
          helperText={errors.userId ?? (users.length === 0 ? t('routes.noFreeSalesReps') : undefined)}
        >
          {users.map((user) => (
            <MenuItem key={user.id} value={String(user.id)}>
              {user.firstName} {user.lastName}
            </MenuItem>
          ))}
        </TextField>
        <PricingModeField value={v.productPricingMode} onChange={(m) => onChange({ productPricingMode: m })} visible={productOffice} />
      </FormSection>

      <FormSection title={t('routes.sectionLoans')}>
        <TextField
          id="route-credit-limit"
          label={t('routes.creditLimit')}
          type="number"
          placeholder="0"
          value={v.creditLimit}
          onChange={text('creditLimit')}
          inputProps={{ min: 0 }}
          required
          fullWidth
          {...err('creditLimit')}
        />
        <TextField id="route-discount-days" label={t('routes.discountDaysRule')} type="number" placeholder="0" value={v.discountDays} onChange={text('discountDays')} inputProps={{ min: 0 }} fullWidth />
        <TextField
          id="route-block-loan"
          type="number"
          label={t('routes.blockLoanAfterInstallments')}
          placeholder={t('routes.blockLoanAfterInstallmentsOff')}
          value={v.blockNewLoanAfterInstallments}
          onChange={(e) => onChange({ blockNewLoanAfterInstallments: e.target.value.replace(/[^0-9]/g, '').slice(0, 3) })}
          inputProps={{ min: 0, max: 365, inputMode: 'numeric' }}
          helperText={t('routes.blockLoanAfterInstallmentsHelp')}
          data-tour="route-block-loan-installments"
          fullWidth
        />
        <FormControlLabel
          sx={{ alignSelf: 'center' }}
          control={<Switch checked={v.allowClientCreditLimit} onChange={(e) => onChange({ allowClientCreditLimit: e.target.checked })} />}
          label={t('routes.useClientCreditLimit')}
        />
      </FormSection>

      <FormSection title={t('routes.sectionFee')}>
        <TextField
          id="route-insurance"
          label={t('routes.feePerBase')}
          type="number"
          placeholder="0"
          value={v.insurance}
          onChange={text('insurance')}
          inputProps={{ min: 0 }}
          helperText={t('routes.feePerBaseHelp')}
          fullWidth
        />
        <TextField
          id="route-insurance-rate"
          label={t('routes.calculationBase')}
          type="number"
          placeholder="0"
          value={v.insuranceRate}
          onChange={text('insuranceRate')}
          inputProps={{ min: 0 }}
          helperText={t('routes.calculationBaseHelp')}
          fullWidth
        />
        <FullRow>
          <FormControlLabel
            control={<Checkbox checked={v.manualInsurance} onChange={(e) => onChange({ manualInsurance: e.target.checked })} />}
            label={t('routes.manualFee')}
          />
        </FullRow>
      </FormSection>

      <FormSection title={t('routes.dailyLimitsTitle')} description={t('routes.dailyLimitsHint')}>
        <TextField
          id="route-max-discount"
          type="number"
          label={t('routes.maxDiscountPerDay')}
          placeholder={t('routes.noLimit')}
          value={v.maxDiscountPerDay}
          onChange={(e) => onChange({ maxDiscountPerDay: e.target.value.replace(/[^0-9.]/g, '').slice(0, 12) })}
          inputProps={{ min: 0, inputMode: 'decimal' }}
          helperText={t('routes.maxDiscountPerDayHelp')}
          data-tour="route-max-discount-per-day"
          fullWidth
        />
        <TextField
          id="route-max-refinances"
          type="number"
          label={t('routes.maxRefinancesPerDay')}
          placeholder={t('routes.noLimit')}
          value={v.maxRefinancesPerDay}
          onChange={(e) => onChange({ maxRefinancesPerDay: e.target.value.replace(/[^0-9]/g, '').slice(0, 3) })}
          inputProps={{ min: 0, max: 365, inputMode: 'numeric' }}
          helperText={t('routes.maxRefinancesPerDayHelp')}
          data-tour="route-max-refinances-per-day"
          fullWidth
        />
        <TextField
          id="route-max-expenses"
          type="number"
          label={t('routes.maxExpensesPerDay')}
          placeholder={t('routes.noLimit')}
          value={v.maxExpensesPerDay}
          onChange={(e) => onChange({ maxExpensesPerDay: e.target.value.replace(/[^0-9.]/g, '').slice(0, 12) })}
          inputProps={{ min: 0, inputMode: 'decimal' }}
          helperText={t('routes.maxExpensesPerDayHelp')}
          data-tour="route-max-expenses-per-day"
          fullWidth
        />
        <FormControlLabel
          sx={{ alignSelf: 'center' }}
          control={<Switch checked={v.requireExpensePhoto} onChange={(e) => onChange({ requireExpensePhoto: e.target.checked })} />}
          label={t('routes.requireExpensePhoto')}
          data-tour="route-require-expense-photo"
        />
      </FormSection>
    </>
  );
};

export default RouteFormSections;
